import { Link } from "react-router-dom";
import { ArrowRight, AlertOctagon, ShieldCheck, FileSignature, Workflow, Network, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";

const pains = [
  { icon: AlertOctagon, title: "It is not just a data problem",
    body: "When a movement is disrupted, the gap is rarely visibility. It is uncertainty over who can decide, on what basis, and with what evidence." },
  { icon: Network, title: "Several organisations, one decision",
    body: "Port, vessel, terminal, haulage and compliance teams have overlapping stakes — but separate systems and separate authorities." },
  { icon: Layers, title: "Existing systems stay in place",
    body: "Operators do not need another central database. They need a coordination and assurance layer that respects the systems already running." },
];

const outcomes = [
  { title: "Faster, cleaner exception resolution",
    body: "Recommended actions are routed to the role that may decide. Less chasing, fewer dead ends." },
  { title: "Decisions with attributable receipts",
    body: "Every consequential action produces a signed receipt: actor, authority, basis, evidence." },
  { title: "Audit-ready by default",
    body: "An evidence pack assembles itself as the response unfolds — ready for regulators, customers and partners." },
];

const steps = [
  { n: "01", t: "Operational issue detected", b: "ETA drift, congestion, documentation friction or a missed cut-off risk surface from existing systems." },
  { n: "02", t: "Recommended actions surfaced", b: "Predictive signals (e.g. SiftyML) and operational rules surface candidate actions — they do not determine authority." },
  { n: "03", t: "KATLAS governance gate", b: "CAR rules check Custody, Authority and policy. The action is allowed, blocked or escalated for named approval." },
  { n: "04", t: "Receipt + evidence pack", b: "A signed receipt is attached to the event. Downstream parties are notified through purpose-bound channels." },
];

export default function Explainer() {
  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12 fc-fade-up">
      {/* Hero */}
      <section className="grid lg:grid-cols-12 gap-10 items-end pb-16 border-b border-[var(--line)]">
        <div className="lg:col-span-8">
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="hero-taxonomy">
            <span className="mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">
              Cierge Use Cases
            </span>
            <span className="text-[var(--ink-soft)] text-[10px]">/</span>
            <span className="mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">
              Supply Chain Use Cases
            </span>
            <span className="text-[var(--ink-soft)] text-[10px]">/</span>
            <span className="mono text-[10px] uppercase tracking-[0.18em] text-[var(--navy)] font-semibold">
              FreightCierge
            </span>
          </div>
          <div className="fc-eyebrow mb-6" data-testid="hero-eyebrow">
            Pilot · Freight Innovation Fund · Sandbox demo
          </div>
          <h1 className="text-[44px] md:text-[64px] leading-[0.95] mb-8" data-testid="hero-title">
            When freight exceptions arise,<br/>
            <span className="text-[var(--ink-soft)]">FreightCierge turns the response into accountable action.</span>
          </h1>
          <p className="text-[17px] leading-relaxed max-w-[58ch] text-[var(--ink)] mb-8" data-testid="hero-lede">
            FreightCierge is a <strong>KATLAS-powered governance layer</strong> for port-corridor freight.
            It does not replace terminal, vessel, telematics or customs systems. It coordinates the
            decisions between them: the right role sees the right evidence, authorised actions produce
            <strong> signed receipts</strong>, and an <strong>evidence pack</strong> forms as the response unfolds.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button asChild data-testid="cta-walkthrough" className="bg-[var(--navy)] hover:bg-black rounded-sm px-5 h-11">
              <Link to="/walkthrough">Take the 90-second walkthrough <ArrowRight size={16} className="ml-2"/></Link>
            </Button>
            <Button asChild variant="outline" data-testid="cta-sandbox" className="rounded-sm px-5 h-11 border-[var(--line)]">
              <Link to="/sandbox">Open the live sandbox</Link>
            </Button>
          </div>
        </div>

        <aside className="lg:col-span-4 fc-card p-6" data-testid="hero-side">
          <div className="fc-eyebrow mb-3">The scenario in this demo</div>
          <div className="display text-[20px] leading-snug mb-3">
            Storm delay, yard congestion, a documentation hold — and a pharma shipment cut-off in 90 minutes.
          </div>
          <ul className="text-sm text-[var(--ink-soft)] leading-relaxed space-y-1.5">
            <li>· Vessel ETA slips by 4 hours</li>
            <li>· Terminal yard at 92% capacity</li>
            <li>· One container under compliance review</li>
            <li>· Multi-party decisions required</li>
          </ul>
          <div className="fc-section-rule pt-4 mt-5">
            <div className="mono text-[11px] uppercase tracking-[0.16em] text-[var(--ink-soft)]">Roles in play</div>
            <div className="text-sm mt-2">Port Ops · Vessel Ops · Terminal Yard · Haulage · Compliance · Escalation</div>
          </div>
        </aside>
      </section>

      {/* Pains */}
      <section className="py-16">
        <div className="fc-eyebrow mb-3">The problem we are solving</div>
        <h2 className="display text-[34px] mb-10 max-w-[24ch]">
          Disruption is rarely a visibility problem. It is a coordination problem.
        </h2>
        <div className="grid md:grid-cols-3 fc-bento" data-testid="pains-grid">
          {pains.map((p, i) => (
            <div key={i} className="p-7" data-testid={`pain-${i}`}>
              <p.icon size={22} className="text-[var(--navy)] mb-4" strokeWidth={1.6}/>
              <div className="display text-[20px] mb-2">{p.title}</div>
              <p className="text-[15px] text-[var(--ink-soft)] leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 border-t border-[var(--line)]">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="fc-eyebrow mb-3">How it works</div>
            <h2 className="display text-[34px] leading-tight mb-4">
              Existing systems feed in. Governed actions and evidence come out.
            </h2>
            <p className="text-[15px] text-[var(--ink-soft)] leading-relaxed max-w-[40ch]">
              FreightCierge sits between the operational signal layer and the action layer.
              The KATLAS node performs runtime CAR governance — Custody, Authority, Receipts —
              before any consequential action is executed.
            </p>
          </div>
          <ol className="lg:col-span-8 grid sm:grid-cols-2 fc-bento">
            {steps.map((s) => (
              <li key={s.n} className="p-7" data-testid={`step-${s.n}`}>
                <div className="mono text-[11px] tracking-[0.18em] text-[var(--ink-soft)] mb-3">STEP {s.n}</div>
                <div className="display text-[19px] mb-2">{s.t}</div>
                <p className="text-[14px] text-[var(--ink-soft)] leading-relaxed">{s.b}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Architecture diagram */}
      <section className="py-16 border-t border-[var(--line)]">
        <div className="fc-eyebrow mb-3">The coordination layer</div>
        <h2 className="display text-[34px] mb-10">A thin governance layer over existing systems.</h2>
        <div className="fc-card p-8" data-testid="arch-diagram">
          <div className="grid lg:grid-cols-5 gap-4 items-stretch">
            {/* Existing systems */}
            <div className="lg:col-span-2 grid grid-cols-2 gap-2">
              {["Terminal Ops", "Vessel/AIS", "Telematics", "Customs/Docs", "Yard Planning", "Haulage TMS"].map(s => (
                <div key={s} className="border border-[var(--line)] p-3 text-[12px] mono text-[var(--ink-soft)] uppercase tracking-wider text-center">
                  {s}
                </div>
              ))}
              <div className="col-span-2 fc-eyebrow text-center pt-1">Existing systems · sources of signal</div>
            </div>

            {/* Arrow */}
            <div className="hidden lg:flex items-center justify-center">
              <ArrowRight size={28} className="text-[var(--ink-soft)]"/>
            </div>

            {/* FreightCierge core */}
            <div className="lg:col-span-2 border-2 border-[var(--navy)] p-5 bg-[#F4F7FB]">
              <div className="mono text-[11px] tracking-[0.18em] text-[var(--navy)]">FREIGHTCIERGE · KATLAS NODE</div>
              <div className="display text-[20px] mt-2 mb-3">CAR runtime governance</div>
              <ul className="text-[13px] space-y-1.5 text-[var(--ink)]">
                <li className="flex gap-2"><ShieldCheck size={14} className="mt-0.5 text-[var(--teal)]"/> <span><strong>Custody</strong> controls the evidence.</span></li>
                <li className="flex gap-2"><Workflow size={14} className="mt-0.5 text-[var(--teal)]"/> <span><strong>Authority</strong> governs the action.</span></li>
                <li className="flex gap-2"><FileSignature size={14} className="mt-0.5 text-[var(--teal)]"/> <span><strong>Receipts</strong> prove what happened.</span></li>
              </ul>
              <div className="fc-section-rule pt-3 mt-4 text-[12px] text-[var(--ink-soft)]">
                KATLAS records the governed action — not a copy of everyone's private data. Evidence stays at source.
              </div>
            </div>
          </div>

          {/* Output row */}
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <div className="border border-[var(--line)] p-5">
              <div className="fc-eyebrow mb-2">Output 1 · Governed actions</div>
              <p className="text-[14px] text-[var(--ink)]">Right role · right authority · right moment. A governed exception response, not an alert feed.</p>
            </div>
            <div className="border border-[var(--line)] p-5">
              <div className="fc-eyebrow mb-2">Output 2 · Clearance-ready evidence</div>
              <p className="text-[14px] text-[var(--ink)]">Signed receipts for freight handoffs and decisions · linked event · downstream notifications.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-16 border-t border-[var(--line)]">
        <div className="fc-eyebrow mb-3">What changes for assessors and operators</div>
        <h2 className="display text-[34px] mb-10 max-w-[26ch]">
          Outcomes a non-technical assessor can see and measure.
        </h2>
        <div className="grid md:grid-cols-3 fc-bento">
          {outcomes.map((o, i) => (
            <div key={i} className="p-7" data-testid={`outcome-${i}`}>
              <div className="mono text-[11px] tracking-[0.18em] text-[var(--ink-soft)] mb-3">OUTCOME 0{i+1}</div>
              <div className="display text-[20px] mb-2">{o.title}</div>
              <p className="text-[14px] text-[var(--ink-soft)] leading-relaxed">{o.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-12 border-t border-[var(--line)]">
        <div className="grid md:grid-cols-2 gap-6 items-end">
          <div>
            <div className="fc-eyebrow mb-2">Ready to see it</div>
            <h3 className="display text-[28px]">Open the live sandbox or take the guided walkthrough.</h3>
          </div>
          <div className="flex gap-3 md:justify-end">
            <Button asChild className="bg-[var(--navy)] hover:bg-black rounded-sm h-11" data-testid="footer-cta-walkthrough">
              <Link to="/walkthrough">Walkthrough</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-sm h-11" data-testid="footer-cta-sandbox">
              <Link to="/sandbox">Live sandbox</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
